// Filters in het adres (MOW-FILTERLINKS-OKT26): lezen en schrijven van de querystring voor de winkel-,
// categorie- en merkpagina. Zo is een selectie (bijvoorbeeld "dames ondergoed van Ten Cate in maat M") een
// link die je kunt kopieren en in een nieuwsbrief zetten, en blijft ze staan bij herladen en Terug.
//
// Bewust zonder imports en met alleen wegstripbare TypeScript, zodat Node dit bestand rechtstreeks laadt
// voor de test (zelfde patroon als lib/klaviyo-core.ts). Een import toevoegen breekt
// scripts/filter-query.test.mjs. De typen hieronder zijn structureel gelijk aan ShopFilters en Product.
//
// Woordenschat (Nederlands, leesbaar; sluit aan op de filterspec ronde2-filters-ui.md 4.9):
//   zoek=boxer   merk=Ten%20Cate,Pastunette   kleur=Zwart   maat=M,XL   materiaal=katoen   pasvorm=Aansluitend,Ruim   eigenschap=stretch,lange-mouw
//   categorie=dames-nachtkleding,heren-nachtkleding (slugs, OF onder elkaar; MOW-CATEGORIEFILTER-OKT26)
//   sale=1 (alleen de sale; op /shop is dat ook de eigen weergave /shop?sale=1)
//   prijs=10-50 (ook 50- of -50)   sort=nieuwste | prijs-laag | prijs-hoog | naam | bestverkocht
//   (bestverkocht is de standaard en wordt alleen geschreven op een pagina waarvan de standaardsortering
//   een andere is: /shop?nieuw=1 opent op nieuwste, dus daar is een bewuste keuze voor bestverkocht
//   alleen met sort=bestverkocht in het adres te bewaren.)
// Alles wat hier niet bij hoort (pagina, nieuw, utm_*, gclid, liveEdit, ...) blijft byte voor byte staan.

export interface FilterQueryState {
  search: string;
  categories: string[];
  sale: boolean;
  brands: string[];
  colors: string[];
  materials: string[];
  /** Model/pasvorm (productveld fit); optioneel zodat oudere aanroepers zonder dit veld blijven werken. */
  fits?: string[];
  /** Eigenschappen (sleutels uit lib/product-properties.ts); optioneel zoals fits. */
  properties?: string[];
  sizes: string[];
  priceMin: number;
  priceMax: number;
  sort: string;
}

/** De velden van een product waar de filters op werken (structureel een deel van Product). */
export interface FilterQueryProduct {
  brand?: string | null;
  mainColor?: string | null;
  mainColors?: ReadonlyArray<unknown> | null;
  materials?: ReadonlyArray<unknown> | null;
  /** Pasvorm; sommige producten hebben er twee, gescheiden door een komma ("Aansluitend, Normaal"). */
  fit?: string | null;
  sizes?: ReadonlyArray<unknown> | null;
  colorOptions?: ReadonlyArray<{ mainColor?: string | null } | null | undefined> | null;
}

// De volgorde is die waarin searchWithFilters ze in het adres zet. Elke sleutel hier valt ook onder noindex
// (NOINDEX_PARAMS in next.config.ts): een filterparameter mag nooit een indexeerbare pagina opleveren.
export const FILTER_QUERY_KEYS = ["zoek", "categorie", "sale", "merk", "kleur", "maat", "materiaal", "pasvorm", "eigenschap", "prijs", "sort"] as const;
export type FilterQueryKey = (typeof FILTER_QUERY_KEYS)[number];

/** Zelfde standaardbereik als DEFAULT_FILTERS in components/ShopSidebar.tsx. */
export const DEFAULT_PRICE_MIN = 0;
export const DEFAULT_PRICE_MAX = 200;
const DEFAULT_SORT = "relevance";

const MAX_VALUES = 12;
const MAX_LENGTH = 80;
const MAX_SEARCH = 100;
const MAX_SLUG = 80;

// Map in plaats van een object: een sleutel als "__proto__" of "constructor" mag niets doen.
const SORT_TO_URL = new Map<string, string>([
  ["newest", "nieuwste"],
  ["price-asc", "prijs-laag"],
  ["price-desc", "prijs-hoog"],
  ["name-asc", "naam"],
]);
const URL_TO_SORT = new Map<string, string>([
  ["nieuwste", "newest"],
  ["prijs-laag", "price-asc"],
  ["prijs-hoog", "price-desc"],
  ["naam", "name-asc"],
  // de interne waarden worden ook geaccepteerd (oude of handgemaakte links)
  ["bestverkocht", DEFAULT_SORT],
  ["relevance", DEFAULT_SORT],
  ["newest", "newest"],
  ["price-asc", "price-asc"],
  ["price-desc", "price-desc"],
  ["name-asc", "name-asc"],
]);

/** Vergelijkingssleutel: kleine letters, zonder accenten, alleen letters en cijfers. "Ten Cate", "ten-cate" en "TEN  CATE" zijn gelijk. */
export function valueKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

type Known = Map<string, string>;

/** "Aansluitend, Normaal" -> ["Aansluitend", "Normaal"]; leeg of geen tekst -> []. Eén plek, ook voor het filter zelf. */
export function splitFit(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value.split(",").map((v) => v.trim()).filter(Boolean);
}

function addKnown(map: Known, value: unknown): void {
  const text = typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
  if (!text) return;
  const key = valueKey(text);
  if (key && !map.has(key)) map.set(key, text);
}

function addAllKnown(map: Known, values: ReadonlyArray<unknown> | null | undefined): void {
  if (!Array.isArray(values)) return;
  for (const v of values) addKnown(map, v);
}

/** De schrijfwijze van merken, kleuren, maten, materialen en pasvormen zoals de producten van de pagina ze hebben. */
export function collectKnown(products: ReadonlyArray<FilterQueryProduct | null | undefined>): {
  brands: Known; colors: Known; sizes: Known; materials: Known; fits: Known;
} {
  const brands: Known = new Map();
  const colors: Known = new Map();
  const sizes: Known = new Map();
  const materials: Known = new Map();
  const fits: Known = new Map();
  for (const p of products ?? []) {
    if (!p) continue;
    addKnown(brands, p.brand);
    addKnown(colors, p.mainColor);
    addAllKnown(colors, p.mainColors);
    for (const o of p.colorOptions ?? []) addKnown(colors, o?.mainColor);
    addAllKnown(sizes, p.sizes);
    addAllKnown(materials, p.materials);
    for (const f of splitFit(p.fit)) addKnown(fits, f);
  }
  return { brands, colors, sizes, materials, fits };
}

function readList(params: URLSearchParams, name: string, known: Known): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (value: string): boolean => {
    const trimmed = value.trim().slice(0, MAX_LENGTH);
    const key = valueKey(trimmed);
    if (!key) return true;
    // Past bij een waarde van de pagina: die schrijfwijze. Anders blijft de waarde zoals getypt staan en
    // geeft het filter 0 resultaten met een zichtbare chip (zoals /shop?merk= altijd deed), in plaats van
    // stil de hele lijst te tonen.
    const name = known.get(key) ?? trimmed;
    if (!seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
    return out.length < MAX_VALUES;
  };
  for (const raw of params.getAll(name)) {
    // Een waarde die als geheel bekend is (ook met een komma erin) wordt niet gesplitst.
    const whole = known.get(valueKey(raw.slice(0, MAX_LENGTH)));
    if (whole) {
      if (!push(whole)) break;
      continue;
    }
    let more = true;
    for (const part of raw.split(",")) {
      more = push(part);
      if (!more) break;
    }
    if (!more) break;
  }
  return out;
}

/** Categorie-slugs uit het adres: kleine letters, cijfers en streepjes; wat daar niet bij past valt weg, nooit een fout. */
function readSlugs(params: URLSearchParams, name: string): string[] {
  const out: string[] = [];
  for (const raw of params.getAll(name)) {
    for (const part of raw.split(",")) {
      const slug = part.trim().toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, MAX_SLUG);
      if (slug && !out.includes(slug)) out.push(slug);
      if (out.length >= MAX_VALUES) return out;
    }
  }
  return out;
}

function parsePrice(raw: string | null): { min: number; max: number } | null {
  if (!raw) return null;
  const m = /^\s*(\d{1,5}(?:[.,]\d{1,2})?)?\s*-\s*(\d{1,5}(?:[.,]\d{1,2})?)?\s*$/.exec(raw);
  if (!m || (m[1] === undefined && m[2] === undefined)) return null;
  const num = (s: string) => Number(s.replace(",", "."));
  const min = m[1] === undefined ? DEFAULT_PRICE_MIN : num(m[1]);
  const max = m[2] === undefined ? DEFAULT_PRICE_MAX : num(m[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
  return { min, max };
}

/**
 * Leest de filters uit een querystring. Geeft alleen de velden terug die in het adres staan en geldig zijn,
 * zodat de aanroeper `{ ...huidig, ...resultaat }` kan doen. Waarden worden naar de schrijfwijze van
 * `products` gebracht (zie readList). `skip` slaat sleutels over (de merkpagina heeft het merk al in de route).
 * Gooit nooit: onleesbare invoer geeft een leeg resultaat.
 */
export function filtersFromQuery(
  search: string,
  products: ReadonlyArray<FilterQueryProduct | null | undefined>,
  options: { skip?: ReadonlyArray<FilterQueryKey>; defaultSort?: string } = {}
): Partial<FilterQueryState> {
  const skip = new Set<string>(options.skip ?? []);
  const pageDefaultSort = options.defaultSort ?? DEFAULT_SORT;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(typeof search === "string" ? search : "");
  } catch {
    return {};
  }
  const known = collectKnown(products);
  const out: Partial<FilterQueryState> = {};

  if (!skip.has("zoek")) {
    const z = (params.get("zoek") ?? "").trim().slice(0, MAX_SEARCH);
    if (z) out.search = z;
  }
  if (!skip.has("categorie")) {
    const v = readSlugs(params, "categorie");
    if (v.length > 0) out.categories = v;
  }
  if (!skip.has("sale") && params.get("sale") === "1") out.sale = true;
  if (!skip.has("merk")) {
    const v = readList(params, "merk", known.brands);
    if (v.length > 0) out.brands = v;
  }
  if (!skip.has("kleur")) {
    const v = readList(params, "kleur", known.colors);
    if (v.length > 0) out.colors = v;
  }
  if (!skip.has("maat")) {
    const v = readList(params, "maat", known.sizes);
    if (v.length > 0) out.sizes = v;
  }
  if (!skip.has("materiaal")) {
    const v = readList(params, "materiaal", known.materials);
    if (v.length > 0) out.materials = v;
  }
  if (!skip.has("pasvorm")) {
    const v = readList(params, "pasvorm", known.fits);
    if (v.length > 0) out.fits = v;
  }
  if (!skip.has("eigenschap")) {
    const v = readSlugs(params, "eigenschap");
    if (v.length > 0) out.properties = v;
  }
  if (!skip.has("prijs")) {
    const p = parsePrice(params.get("prijs"));
    if (p) {
      out.priceMin = p.min;
      out.priceMax = p.max;
    }
  }
  if (!skip.has("sort")) {
    const s = URL_TO_SORT.get((params.get("sort") ?? "").trim().toLowerCase());
    // Een sortering in het adres is een bewuste keuze. De standaard (bestverkocht) hoeft alleen mee te komen
    // op een pagina die zelf op iets anders opent; elders is zij de beginstand en dus niets te zetten.
    if (s && (s !== DEFAULT_SORT || pageDefaultSort !== DEFAULT_SORT)) out.sort = s;
  }
  return out;
}

function fmtNumber(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/**
 * Zet de filters in een querystring: de eigen sleutels (zie FILTER_QUERY_KEYS) gaan eruit en komen, voor zover ze
 * van de standaard afwijken, in vaste volgorde achteraan terug. Alle andere parameters blijven byte voor byte en
 * in dezelfde volgorde staan; er wordt niets opnieuw gecodeerd. Geeft de nieuwe querystring terug, met "?"
 * ervoor, of "" als er niets overblijft. Gooit nooit.
 */
export function searchWithFilters(
  search: string,
  filters: FilterQueryState,
  options: { skip?: ReadonlyArray<FilterQueryKey>; defaultSort?: string } = {}
): string {
  const own = new Set<string>(FILTER_QUERY_KEYS);
  const rest = String(search ?? "")
    .replace(/^\?/, "")
    .split("&")
    .filter(Boolean)
    .filter((part) => {
      const raw = part.split("=")[0];
      let key = raw;
      try {
        key = decodeURIComponent(raw.replace(/\+/g, " "));
      } catch {
        // Ongeldige %-reeks: de ruwe sleutel vergelijken, nooit een fout.
      }
      return !own.has(key) || (options.skip ?? []).includes(key as FilterQueryKey);
    });

  const skip = new Set<string>(options.skip ?? []);
  const list = (values: ReadonlyArray<string> | undefined) =>
    (values ?? [])
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean)
      .slice(0, MAX_VALUES)
      .map(encodeURIComponent)
      .join(",");

  const add = (key: FilterQueryKey, value: string) => {
    if (value && !skip.has(key)) rest.push(`${key}=${value}`);
  };

  const z = (filters.search ?? "").trim().slice(0, MAX_SEARCH);
  add("zoek", z ? encodeURIComponent(z) : "");
  add("categorie", list(filters.categories));
  add("sale", filters.sale ? "1" : "");
  add("merk", list(filters.brands));
  add("kleur", list(filters.colors));
  add("maat", list(filters.sizes));
  add("materiaal", list(filters.materials));
  add("pasvorm", list(filters.fits));
  add("eigenschap", list(filters.properties));
  const min = Number.isFinite(filters.priceMin) ? filters.priceMin : DEFAULT_PRICE_MIN;
  const max = Number.isFinite(filters.priceMax) ? filters.priceMax : DEFAULT_PRICE_MAX;
  if (min > DEFAULT_PRICE_MIN || max < DEFAULT_PRICE_MAX) add("prijs", `${fmtNumber(min)}-${fmtNumber(max)}`);
  // De standaardsortering van de pagina wordt weggelaten; elke andere keuze komt in het adres, ook
  // "bestverkocht" op een pagina die zelf op iets anders opent (zie de kop van dit bestand).
  const pageDefaultSort = options.defaultSort ?? DEFAULT_SORT;
  if (filters.sort !== pageDefaultSort) {
    add("sort", SORT_TO_URL.get(filters.sort) ?? (filters.sort === DEFAULT_SORT ? "bestverkocht" : ""));
  }
  return rest.length > 0 ? "?" + rest.join("&") : "";
}

const sameList = (x: ReadonlyArray<string> | undefined, y: ReadonlyArray<string> | undefined) => {
  const a = x ?? [];
  const b = y ?? [];
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

/** Is er alleen aan de zoekterm iets veranderd (typen)? Dan hoeft het adres niet bij elke toets bijgewerkt. */
export function onlySearchChanged(a: FilterQueryState, b: FilterQueryState): boolean {
  return (
    a.search !== b.search &&
    sameList(a.categories, b.categories) &&
    !!a.sale === !!b.sale &&
    sameList(a.brands, b.brands) &&
    sameList(a.colors, b.colors) &&
    sameList(a.materials, b.materials) &&
    sameList(a.fits, b.fits) &&
    sameList(a.properties, b.properties) &&
    sameList(a.sizes, b.sizes) &&
    a.priceMin === b.priceMin &&
    a.priceMax === b.priceMax &&
    a.sort === b.sort
  );
}

/** Is er alleen aan het prijsbereik iets veranderd (slepen aan de schuif)? Zelfde reden als typen. */
export function onlyPriceChanged(a: FilterQueryState, b: FilterQueryState): boolean {
  return (
    (a.priceMin !== b.priceMin || a.priceMax !== b.priceMax) &&
    a.search === b.search &&
    sameList(a.categories, b.categories) &&
    !!a.sale === !!b.sale &&
    sameList(a.brands, b.brands) &&
    sameList(a.colors, b.colors) &&
    sameList(a.materials, b.materials) &&
    sameList(a.fits, b.fits) &&
    sameList(a.properties, b.properties) &&
    sameList(a.sizes, b.sizes) &&
    a.sort === b.sort
  );
}
